import { useState } from 'react';

import { ProjectSelect } from '@/components/project-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SubscriberSource } from '@/lib/subscribers';

export interface SubscriberValues {
  email: string;
  name: string;
  source: SubscriberSource;
  project: string;
}

interface SubscriberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: SubscriberValues) => void;
}

/** Manually add someone to the mailing list (e.g. when they contact you). */
export function SubscriberDialog({ open, onOpenChange, onSubmit }: SubscriberDialogProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [source, setSource] = useState<SubscriberSource>('jury');
  const [project, setProject] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({ email, name, source, project });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Add subscriber</DialogTitle>
            <DialogDescription>Add someone to the mailing list.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="subscriber-email">Email</Label>
            <Input
              id="subscriber-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="subscriber-name">Name</Label>
            <Input
              id="subscriber-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="grid gap-2">
            <Label>Source</Label>
            <Select value={source} onValueChange={(value) => setSource(value as SubscriberSource)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jury">Jury</SelectItem>
                <SelectItem value="post_vote">Post-vote</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {source === 'post_vote' && (
            <div className="grid gap-2">
              <Label>Project</Label>
              <ProjectSelect value={project} onChange={(option) => setProject(option.name)} />
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Add subscriber</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
